import React, { useState, useEffect, useCallback } from 'react';
import { HuePicker } from 'react-color';
import { useTranslation, getCurrentLanguage } from '../../utils/languageUtils';
import './BackgroundTab.css';

const BackgroundTab = ({ konvaController, configData, selectedTheme }) => {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const [selectedBg, setSelectedBg] = useState('custom');
  const [customColor, setCustomColor] = useState('#4a90e2');
  const [backgroundSets, setBackgroundSets] = useState([]);
  const [selectedBackgroundSet, setSelectedBackgroundSet] = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('color'); // 'color' | 'image'
  const [hasBgImage, setHasBgImage] = useState(false);
  const [hasBgColor, setHasBgColor] = useState(false);
  
  // Default background options
  const defaultBackgroundOptions = [
    { id: 'transparent', name: t('background.transparent', 'Transparent'), color: 'transparent', border: '1px dashed #ccc' },
    { id: 'white', name: t('background.white', 'White'), color: '#ffffff' },
    { id: 'black', name: t('background.black', 'Black'), color: '#000000' },
    { id: 'gray', name: t('background.gray', 'Gray'), color: '#f0f0f0' },
    { id: 'blue', name: t('background.blue', 'Blue'), color: '#0000FF' },
    { id: 'custom', name: t('background.custom', 'Custom'), color: customColor },
  ];

  // Fetch backgrounds from CSV
  const fetchBackgrounds = useCallback(async (url, setName) => {
    if (!url) {
      console.log('DEBUG: No background URL provided');
      return [];
    }

    setLoading(true);
    try {
      console.log(`DEBUG: Fetching backgrounds from ${url}`);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch backgrounds: ${response.status}`);
      }
      
      const csvText = await response.text();
      const lines = csvText.split('\n').filter(line => line.trim());
      
      console.log(`DEBUG: CSV content (first 100 chars): ${csvText.substring(0, 100)}...`);
      console.log(`DEBUG: Found ${lines.length} lines in CSV`);
      
      // Skip header line if it exists
      const hasHeader = lines[0] && (lines[0].toLowerCase().includes('url') || 
                                    lines[0].toLowerCase().includes('image'));
      const startIndex = hasHeader ? 1 : 0;
      
      if (hasHeader) {
        console.log(`DEBUG: Detected header row: ${lines[0]}`);
      }
      
      // Extract base URL from the CSV URL to use for relative paths
      // Example: https://dev-bucket-plugcustom.oss-cn-hongkong.aliyuncs.com/QP_Group/Backgrounds/...
      const urlParts = url.split('/');
      // Remove the last part (filename) and join back
      const baseUrl = urlParts.slice(0, urlParts.length - 1).join('/');
      console.log(`DEBUG: Using base URL for relative paths: ${baseUrl}`);
      
      const backgroundUrls = lines.slice(startIndex).map((line, index) => {
        // Try to extract URL from CSV line
        let extractedUrl = '';
        
        // First try comma separation
        const commaParts = line.split(',');
        if (commaParts.length > 0) {
          extractedUrl = commaParts[0].trim();
        } else {
          // If no comma, use the whole line as URL
          extractedUrl = line.trim();
        }
        
        // Ensure URL is valid
        if (!extractedUrl) {
          console.warn(`DEBUG: Empty URL found in line ${index + startIndex}: ${line}`);
          return null;
        }
        
        // Convert relative URLs to absolute URLs
        let fullUrl = extractedUrl;
        
        // If it's a relative URL (doesn't start with http:// or https://)
        if (!extractedUrl.startsWith('http://') && !extractedUrl.startsWith('https://')) {
          // If it starts with a slash, remove it before joining with baseUrl
          if (extractedUrl.startsWith('/')) {
            extractedUrl = extractedUrl.substring(1);
          }
          
          // Check if the URL is from the export_urls.csv format (just filename) or a relative path
          if (!extractedUrl.includes('/')) {
            // It's likely just a filename, so we need to use the base URL
            fullUrl = `${baseUrl}/${extractedUrl}`;
          } else {
            // It's a relative path, so we need to determine the correct base
            // For URLs from Aliyun OSS, we need to use the QP_Group base
            if (baseUrl.includes('dev-bucket-plugcustom.oss-cn-hongkong.aliyuncs.com')) {
              // Extract the base up to QP_Group
              const ossBaseParts = baseUrl.split('QP_Group');
              if (ossBaseParts.length > 0) {
                fullUrl = `${ossBaseParts[0]}QP_Group/${extractedUrl}`;
              } else {
                fullUrl = `${baseUrl}/${extractedUrl}`;
              }
            } else {
              fullUrl = `${baseUrl}/${extractedUrl}`;
            }
          }
        }
        
        console.log(`DEBUG: Extracted background URL: ${extractedUrl} -> ${fullUrl}`);
        
        return {
          id: `bg_${setName}_${index}`,
          name: `${setName} ${index + 1}`,
          url: fullUrl,
          originalUrl: extractedUrl,
          isImage: true,
          setName: setName
        };
      }).filter(item => item !== null); // Remove any null entries
      
      console.log(`DEBUG: Loaded ${backgroundUrls.length} backgrounds from ${setName}`);
      return backgroundUrls;
    } catch (error) {
      console.error('Error fetching backgrounds:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync UI state with actual canvas background status
  const refreshBackgroundStates = useCallback(() => {
    try {
      const img = !!(konvaController && konvaController.hasBackgroundImage && konvaController.hasBackgroundImage());
      const col = !!(konvaController && konvaController.hasBackgroundColor && konvaController.hasBackgroundColor());

      setHasBgImage(img);
      setHasBgColor(col);

      // Prefer image mode if an image exists
      if (img && mode !== 'image') {
        setMode('image');
        return;
      }
      // If no image but color exists, align to color mode
      if (!img && col && mode !== 'color') {
        setMode('color');
        return;
      }
      // If neither exists, do not force a mode change; keep user selection
    } catch (e) {
      // ignore
    }
  }, [konvaController, mode]);

  // Re-evaluate background state when controller reference changes
  useEffect(() => {
    refreshBackgroundStates();
  }, [konvaController, refreshBackgroundStates]);

  // Load ALL background sets from config data
  useEffect(() => {
    if (!configData || !configData.Themes) {
      console.log('DEBUG: No config data available, clearing background sets');
      setBackgroundSets([]);
      return;
    }
    
    console.log('DEBUG: Loading ALL background sets from all themes');
    const allSets = [];
    
    // Loop through all themes to collect all background sets
    configData.Themes.forEach(theme => {
      // Check for BackgroundsCsv array in each theme
      if (theme.BackgroundsCsv && Array.isArray(theme.BackgroundsCsv)) {
        console.log(`DEBUG: Found ${theme.BackgroundsCsv.length} background sets in theme ${theme.ThemeName}`);
        
        theme.BackgroundsCsv.forEach(set => {
          if (set.Name && set.Url) {
            // Check if this set is already added (by URL to avoid duplicates)
            const existingSet = allSets.find(existing => existing.url === set.Url);
            if (!existingSet) {
              allSets.push({
                name: set.Name,
                url: set.Url,
                id: `bg_${set.Name.replace(/\s+/g, '_').toLowerCase()}_${allSets.length}`,
                themeName: theme.ThemeName
              });
            }
          } else {
            console.warn('DEBUG: Invalid background set in BackgroundsCsv - missing Name or Url:', set);
          }
        });
      }

      // Check for legacy Tab structure in each theme
      if (theme.Tab && Array.isArray(theme.Tab)) {
        const backgroundTab = theme.Tab.find(tab => 
          tab.TabName?.en === 'Background' || 
          tab.TabName?.cn === '背景' || 
          tab.TabType === 'Background'
        );

        if (backgroundTab && backgroundTab.Urls && Array.isArray(backgroundTab.Urls)) {
          console.log(`DEBUG: Found ${backgroundTab.Urls.length} backgrounds in legacy Tab structure for theme ${theme.ThemeName}`);
          
          // Get a localized name for the background set if available
          const tabName = backgroundTab.TabName?.[currentLang] || 
                        backgroundTab.TabName?.en || 
                        backgroundTab.TabName?.cn || 
                        'Backgrounds';
          
          // Check if this set is already added (by first URL to avoid duplicates)
          const firstUrl = backgroundTab.Urls[0];
          const existingSet = allSets.find(existing => existing.url === firstUrl);
          
          if (!existingSet && firstUrl) {
            allSets.push({
              name: `${tabName} (${theme.ThemeName})`,
              url: firstUrl, // Use the first URL as a CSV source
              legacyUrls: backgroundTab.Urls, // Store all URLs for direct use
              id: `legacy_bg_${tabName.replace(/\s+/g, '_').toLowerCase()}_${allSets.length}`,
              themeName: theme.ThemeName
            });
          }
        }
      }
    });

    setBackgroundSets(allSets);
    console.log(`DEBUG: Loaded ${allSets.length} total background sets from all themes`);

    // Auto-select first set if available
    if (allSets.length > 0) {
      // Store the current selected set ID for comparison
      const currentSetId = selectedBackgroundSet?.id;
      const currentSetName = selectedBackgroundSet?.name;
      
      // If we already have a selected set, try to find it in the new sets
      if (currentSetId || currentSetName) {
        const matchingSet = allSets.find(set => 
          set.id === currentSetId || 
          set.name === currentSetName
        );
        
        if (matchingSet) {
          // Keep the same set selected if it exists
          setSelectedBackgroundSet(matchingSet);
          console.log('DEBUG: Keeping previously selected background set:', matchingSet.name);
          return;
        }
      }
      
      // Otherwise select the first set
      setSelectedBackgroundSet(allSets[0]);
      console.log('DEBUG: Auto-selected first background set:', allSets[0].name);
    } else {
      console.log('DEBUG: No background sets available');
      setSelectedBackgroundSet(null);
    }
  }, [configData, currentLang, selectedBackgroundSet?.id, selectedBackgroundSet?.name]);

  // Load backgrounds when a set is selected
  useEffect(() => {
    if (!selectedBackgroundSet) {
      console.log('DEBUG: No background set selected, clearing backgrounds');
      setBackgrounds([]);
      return;
    }

    // Track if this is a mounted component to prevent state updates after unmount
    let isMounted = true;
    
    // Store the current URL to prevent duplicate loads
    const currentUrl = selectedBackgroundSet.url;
    const currentLegacyUrls = selectedBackgroundSet.legacyUrls;
    
    // Add a cache key to prevent duplicate loads
    const cacheKey = selectedBackgroundSet.id || 
                    `${selectedBackgroundSet.name}_${currentUrl || JSON.stringify(currentLegacyUrls || [])}`;
    
    console.log(`DEBUG: Loading backgrounds for set: ${selectedBackgroundSet.name} with cache key: ${cacheKey}`);

    const loadBackgrounds = async () => {
      // We've removed the check for already loaded backgrounds since it was causing issues
      // with the dependency array and unnecessary re-renders
      
      setLoading(true);
      let loadedBackgrounds = [];

      try {
        if (currentLegacyUrls && Array.isArray(currentLegacyUrls)) {
          // Handle legacy URLs directly
          loadedBackgrounds = currentLegacyUrls.map((url, index) => ({
            id: `legacy_bg_${selectedBackgroundSet.id || ''}_${index}`,
            name: `${selectedBackgroundSet.name} ${index + 1}`,
            url: url,
            isImage: true,
            setName: selectedBackgroundSet.name,
            cacheKey: cacheKey
          }));
          console.log(`DEBUG: Loaded ${loadedBackgrounds.length} legacy backgrounds directly`);
        } else if (currentUrl) {
          // Fetch from CSV
          console.log(`DEBUG: Fetching backgrounds from CSV URL: ${currentUrl}`);
          try {
            // Make sure URL is properly formatted
            let formattedUrl = currentUrl.trim();
            if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
              // If URL is relative, make it absolute
              if (formattedUrl.startsWith('/')) {
                formattedUrl = window.location.origin + formattedUrl;
              } else {
                formattedUrl = window.location.origin + '/' + formattedUrl;
              }
              console.log(`DEBUG: Formatted URL to absolute path: ${formattedUrl}`);
            }
            
            loadedBackgrounds = await fetchBackgrounds(formattedUrl, selectedBackgroundSet.name);
            console.log(`DEBUG: Successfully loaded ${loadedBackgrounds.length} backgrounds from CSV`);
            
            // Add cache key to each background
            loadedBackgrounds = loadedBackgrounds.map(bg => ({
              ...bg,
              cacheKey: cacheKey
            }));
          } catch (csvError) {
            console.error('DEBUG: Failed to load backgrounds from CSV:', csvError);
            // Try to load the URL directly as an image if CSV parsing fails
            console.log('DEBUG: Attempting to use URL directly as an image');
            loadedBackgrounds = [{
              id: `direct_bg_${selectedBackgroundSet.id || ''}_0`,
              name: `${selectedBackgroundSet.name}`,
              url: currentUrl,
              isImage: true,
              setName: selectedBackgroundSet.name,
              cacheKey: cacheKey
            }];
          }
        } else {
          console.warn('DEBUG: Selected background set has no URL or legacy URLs:', selectedBackgroundSet);
        }
      } catch (error) {
        console.error('DEBUG: Error loading backgrounds:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }

      if (isMounted) {
        setBackgrounds(loadedBackgrounds);
        console.log(`DEBUG: Set ${loadedBackgrounds.length} backgrounds for set: ${selectedBackgroundSet.name}`);
      }
    };

    loadBackgrounds();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectedBackgroundSet, fetchBackgrounds]);


  // No UI toggle; if config/state prefers image view, ensure color background is cleared
  // This keeps canvas clean under image backgrounds
  // Note: we only remove color when in image view, we do not auto-apply any image.
  // Users still select an image from the list.
  

  // Handle background selection
  const handleBackgroundSelect = (option) => {
    setSelectedBg(option.id);
    console.log('DEBUG: Selected background:', option);
    
    // Apply the background to the canvas via controller
    if (konvaController) {
      if (option.isImage) {
        // Apply image background
        konvaController.setBackgroundImage(option.url);
        console.log('DEBUG: Applied background image to canvas:', option.url);
        setMode('image');
      } else {
        // Apply color background
        konvaController.setBackground(option.color);
        console.log('DEBUG: Applied background color to canvas:', option.color);
        setMode('color');
      }
      refreshBackgroundStates();
    } else {
      console.warn('KonvaController not available, background change is UI-only');
    }
  };

  // Handle background set selection
  const handleBackgroundSetSelect = (set) => {
    console.log('DEBUG: Selected background set:', set);
    setSelectedBackgroundSet(set);
  };

  // Determine availability of options for each mode
  const hasImageOptions = (Array.isArray(backgroundSets) && backgroundSets.length > 0) || (Array.isArray(backgrounds) && backgrounds.length > 0);
  const hasColorOptions = Array.isArray(defaultBackgroundOptions) && defaultBackgroundOptions.length > 0;
  // Prioritize image view when image options exist (config-driven), else fallback to color
  const viewMode = (hasImageOptions || hasBgImage) ? 'image' : 'color';

  useEffect(() => {
    if (konvaController && viewMode === 'image') {
      try {
        if (konvaController.removeBackgroundColor) {
          konvaController.removeBackgroundColor();
        }
        refreshBackgroundStates();
      } catch (e) {
        console.warn('Failed to remove background color in image view:', e);
      }
    }
  }, [konvaController, viewMode, refreshBackgroundStates]);

  return (
    <div className="background-tab">
      {/* Toggle removed: mode is determined by config/state */}

      {/* Background Set Selection */}
      {viewMode === 'image' && backgroundSets.length > 0 && (
        <div className="background-sets-container">
          <div className="background-sets-carousel">
            {backgroundSets.length > 1 && (
              <button
                className="background-set-nav-arrow prev"
                onClick={() => {
                  const container = document.querySelector('.background-sets');
                  if (container) {
                    container.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                aria-label={t('previousBackgroundSets', 'Previous sets')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            
            {/* Only show background sets container if there's more than one option */}
            {backgroundSets.length > 1 && (
              <div className="background-sets">
                {backgroundSets.map((set, index) => (
                  <div 
                    key={set.id || `set_${index}`}
                    className={`background-set ${selectedBackgroundSet && (selectedBackgroundSet.id === set.id || selectedBackgroundSet.name === set.name) ? 'selected' : ''}`}
                    onClick={() => handleBackgroundSetSelect(set)}
                  >
                    <span>{set.name}</span>
                  </div>
                ))}
              </div>
            )}
            
            {backgroundSets.length > 1 && (
              <button
                className="background-set-nav-arrow next"
                onClick={() => {
                  const container = document.querySelector('.background-sets');
                  if (container) {
                    container.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                aria-label={t('nextBackgroundSets', 'Next sets')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Color Options (default mode) */}
      {viewMode === 'color' && (
        <div className="middle-option-container">
          <div className="middle-option">
            <div className="background-options">
              {defaultBackgroundOptions.map((bg) => (
                <div 
                  key={bg.id}
                  className={`background-option ${selectedBg === bg.id ? 'selected' : ''}`}
                  onClick={() => handleBackgroundSelect(bg)}
                  title={bg.name}
                >
                  <div 
                    className="color-preview" 
                    style={{ 
                      backgroundColor: bg.color,
                      border: bg.border || 'none'
                    }}
                  />
                  <span className="background-option-name">{bg.name}</span>
                </div>
              ))}
            </div>

            {selectedBg === 'custom' && (
              <div className="full-width-color-picker">
                <div className="picker-container">
                  <HuePicker
                    color={customColor}
                    onChange={(color) => {
                      const next = color.hex;
                      setCustomColor(next);
                      // Live-apply background color while dragging the slider
                      try {
                        handleBackgroundSelect({ id: 'custom', color: next });
                      } catch (err) {
                        console.warn('BackgroundTab: failed to live-apply custom color', err);
                      }
                    }}
                    width="100%"
                    height="12px"
                  />

                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Theme Background Images (only in Image mode) */}
      {viewMode === 'image' && backgrounds.length > 0 && (
        <div className="theme-backgrounds-container">
          {loading ? (
            <div className="loading-backgrounds">{t('background.loading', 'Loading backgrounds...')}</div>
          ) : (
            <div className="theme-backgrounds">
              {backgrounds.map((bg) => (
                <div 
                  key={bg.id}
                  className={`theme-background ${selectedBg === bg.id ? 'selected' : ''}`}
                  onClick={() => handleBackgroundSelect(bg)}
                  title={bg.name}
                >
                  <div 
                    className="background-image-preview" 
                    style={{ 
                      backgroundImage: `url(${bg.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BackgroundTab;
